export type ToastSeverity = "error" | "success" | "info" | "warning";

export interface ParsedWalletError {
  severity: ToastSeverity;
  message: string;
  isUserCancel: boolean;
  isIntentExpired: boolean;
}

const truncate = (input: string, max = 180) =>
  input.length > max ? `${input.slice(0, max)}…` : input;

/**
 * Normalizes a raw error coming from wagmi / ethers / MetaMask into a
 * user-friendly toast payload.
 *
 * @param error     The caught error (unknown).
 * @param context   Short label describing what the user was trying to do
 *                  (e.g. "Approve USDC", "Mint USDC"). Used only as the
 *                  prefix for the fallback "<context> failed: …" message.
 */
export const parseWalletError = (
  error: unknown,
  context = "Transaction",
): ParsedWalletError => {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Unknown error";
  const msg = raw.toLowerCase();

  // User dismissed the wallet prompt - treat as a soft warning, not an error.
  if (
    msg.includes("user rejected") ||
    msg.includes("user denied") ||
    msg.includes("action_rejected") ||
    msg.includes("user cancel")
  ) {
    return {
      severity: "warning",
      message: `${context} cancelled - no transaction was sent.`,
      isUserCancel: true,
      isIntentExpired: false,
    };
  }

  // RPC rate limiting.
  if (
    msg.includes("429") ||
    msg.includes("too many requests") ||
    msg.includes("rate limit")
  ) {
    return {
      severity: "error",
      message: "RPC rate limit exceeded. Please wait a moment and try again.",
      isUserCancel: false,
      isIntentExpired: false,
    };
  }

  // Authorization expired - caller often needs to clear cached signatures.
  if (msg.includes("intent expired") || msg.includes("expired")) {
    return {
      severity: "error",
      message: "Authorization expired. Please sign again to continue.",
      isUserCancel: false,
      isIntentExpired: true,
    };
  }

  if (msg.includes("insufficient funds")) {
    return {
      severity: "error",
      message: "Insufficient funds to cover gas fees.",
      isUserCancel: false,
      isIntentExpired: false,
    };
  }

  if (msg.includes("insufficient")) {
    return {
      severity: "error",
      message: "Insufficient balance or allowance for this action.",
      isUserCancel: false,
      isIntentExpired: false,
    };
  }

  if (msg.includes("interval not met")) {
    return {
      severity: "error",
      message: "Payment interval not met. Please wait before paying again.",
      isUserCancel: false,
      isIntentExpired: false,
    };
  }

  if (msg.includes("timeout")) {
    return {
      severity: "error",
      message: "Request timed out. Please try again.",
      isUserCancel: false,
      isIntentExpired: false,
    };
  }

  if (msg.includes("network") || msg.includes("connection")) {
    return {
      severity: "error",
      message: "Network connection issue. Please check your connection.",
      isUserCancel: false,
      isIntentExpired: false,
    };
  }

  // Fallback - include the raw message so we don't swallow unknown errors,
  // but keep it short and prefixed with the calling context.
  return {
    severity: "error",
    message: `${context} failed: ${truncate(raw)}`,
    isUserCancel: false,
    isIntentExpired: false,
  };
};
